import React, { useContext } from 'react';
import { Dropdown } from 'components/ui';
import { AIContext } from 'contexts/AIContext';
import { HiChevronDown, HiStar } from 'react-icons/hi';
import AIPriceBadge from 'components/shared/AIPriceBadge';
import { BiSolidPencil } from 'react-icons/bi';
import { motion } from 'framer-motion';

/**
 * ImageChatModelSelector Component
 * 
 * Dropdown to select AI models that support image editing
 * Shows only models with allowImageEdit: true
 */
const ImageChatModelSelector = ({ 
    selectedModel, 
    onModelChange,
    className = "",
    compact = false
}) => {
    const { aiImageModels } = useContext(AIContext);
    
    // Filter only models that support image editing and are not disabled
    const editableModels = aiImageModels?.filter(m => 
        m.allowImageEdit && !m.disabled && !m.hidden
    ) || [];
    
    const currentModel = editableModels.find(m => m.model === selectedModel) || editableModels[0];
    
    if (!editableModels?.length) return null;

    const Toggle = (
        <motion.div 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`
                flex items-center justify-between gap-2 cursor-pointer
                px-3 py-2 rounded-xl
                bg-gradient-to-br from-white/80 to-white/60 
                dark:from-gray-800/80 dark:to-gray-900/80
                border border-pink-200/50 dark:border-pink-500/20
                backdrop-blur-xl shadow-lg shadow-pink-500/5
                hover:shadow-pink-500/10 hover:border-pink-300/60 dark:hover:border-pink-500/30
                transition-all duration-300
                ${className}
            `}
        >
            <div className="flex items-center gap-2 min-w-0">
                <img 
                    src={currentModel?.image} 
                    alt={currentModel?.driverDisplayName}
                    className="w-4 h-4 rounded flex-shrink-0"
                />
                <div className="flex flex-col items-start min-w-0">
                    <div className="flex items-center gap-1.5">
                        <span className={`font-semibold text-gray-800 dark:text-gray-200 ${compact ? 'text-xs' : 'text-sm'} truncate`}>
                            {compact && currentModel?.name?.length > 12 
                                ? currentModel?.name?.slice(0, 12) + '...' 
                                : currentModel?.name
                            }
                        </span>
                        {currentModel?.best && (
                            <HiStar className="w-3 h-3 text-amber-500 flex-shrink-0" />
                        )}
                    </div>
                    {!compact && (
                        <AIPriceBadge
                            price={currentModel?.price || 0}
                            quantity={1}
                            priceType="generation"
                            size="sm"
                            variant="pink"
                            animate={false}
                        />
                    )}
                </div>
            </div>
            <HiChevronDown className="text-lg text-gray-600 dark:text-gray-400 flex-shrink-0" />
        </motion.div>
    );

    return (
        <Dropdown 
            renderTitle={Toggle}
            placement="top-end"
            className="!w-full"
            menuClass="max-h-[280px] overflow-y-auto !bg-white/95 dark:!bg-gray-900/95 backdrop-blur-xl !border-pink-200/50 dark:!border-pink-500/20 !shadow-2xl"
        >
            {editableModels.map((model) => (
                <Dropdown.Item 
                    key={model.model}
                    eventKey={model.model} 
                    onSelect={() => onModelChange(model.model)}
                    className={`
                        flex items-center gap-3 p-3 
                        hover:!bg-pink-50/80 dark:hover:!bg-pink-900/20
                        transition-all duration-200
                        ${selectedModel === model.model ? 'bg-pink-100/50 dark:bg-pink-900/30' : ''}
                    `}
                >
                    <img 
                        src={model.image} 
                        alt={model.driverDisplayName}
                        className="w-5 h-5 rounded flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm">
                                {model.name}
                            </span>
                            {model.best && (
                                <HiStar className="w-3 h-3 text-amber-500 flex-shrink-0" />
                            )}
                            <BiSolidPencil className="w-3 h-3 text-pink-500 flex-shrink-0" />
                        </div>
                        <div className="flex items-center gap-2">
                            <AIPriceBadge
                                price={model.price || 0}
                                quantity={1}
                                priceType="generation"
                                size="sm"
                                variant="pink"
                                animate={false}
                            />
                            {model.maxInputImages && (
                                <span className="text-[10px] text-pink-600 dark:text-pink-400">
                                    • Max {model.maxInputImages} images
                                </span>
                            )}
                        </div>
                    </div>
                </Dropdown.Item>
            ))}
        </Dropdown>
    );
};

export default ImageChatModelSelector;

